import React, { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import qualifications from "../../utils/qualifications";
import axios from "../../axios/AxiosInstance";
import { Label } from "./Label";
import { Input } from "./Input";
import { FixedSizeList as List } from "react-window";
import LazyLoadingSelect from "./LazyLoadingSelect";
import { toast } from "react-toastify";

export const AddInterviewModal = ({ isOpen, onClose, candidate }) => {
  const [formData, setFormData] = useState({
    date: null,
    hour: "",
    client: "",
    recruteur: "",
    type: "présentiel",
    comment: "",
    place: "",
    candidate: candidate._id,
  });

  const [recruiters, setRecruiters] = useState([]);
  const [clients, setClients] = useState([]);
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const timerRef = useRef(null); // Use useRef to hold the debounce timer

  useEffect(() => {
    getAgencyUsers();
  }, []);

  // Handle client selection
  const handleClientSelection = (client) => {
    setFormData({ ...formData, client: client._id });
    setClientSearch(client.companyName); // set the search text to the selected client name
    setShowClientDropdown(false); // hide the dropdown after selection
  };

  // Search clients with debouncing
  const searchClient = (word) => {
    console.log(word);

    setClientSearch(word);
    if (word.length === 0) {
      setClients([]);
    }

    // Do not trigger search if word is too short
    if (word.length < 3) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // Clear the previous timer and create a new one
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setIsLoadingClient(true);
      axios
        .post("client/freelance/find-by-text", { query: word })
        .then((res) => {
          setClients(res.data);
          setShowClientDropdown(true);
          console.log(res.data);
        })
        .catch((e) => console.log(e))
        .finally(() => setIsLoadingClient(false));
    }, 700); // 700ms debounce time
  };

  // Fetch agency users
  const getAgencyUsers = () => {
    axios("user/active").then((res) => {
      setRecruiters(res.data);
      console.log(res.data);
    });
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    axios
      .post("entretien", formData)
      .then((res) => {
        toast.success("RDV créé! ");
        onClose();
      })
      .catch((e) => {
        toast.error("Une erreur s'est produite ! ");
        console.log(e);
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Planifier un entretien"
      className="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Client */}
        <div className="space-y-2">
          <Label htmlFor="client" className="text-sm font-medium text-gray-700">
            Client concerné
          </Label>
          <div className="relative">
            <Input
              value={clientSearch}
              onChange={(e) => searchClient(e.target.value)}
              placeholder="Rechercher le client"
              className="w-full border border-gray-300 rounded-md pr-10"
            />
            {isLoadingClient && (
              <div className="absolute inset-y-0 right-3 flex items-center">
                <svg
                  className="animate-spin h-4 w-4 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  ></path>
                </svg>
              </div>
            )}
          </div>

          {showClientDropdown && clients.length > 0 && (
            <ul className="absolute z-10 mt-1 w-[400px] bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
              {clients.map((client) => (
                <li
                  key={client._id}
                  onClick={() => handleClientSelection(client)}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                    formData.client?._id === client._id
                      ? "bg-indigo-100 font-semibold"
                      : ""
                  }`}
                >
                  {client.companyName}
                </li>
              ))}
            </ul>
          )}

          {formData.client && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                Client sélectionné:
              </span>
              <span className="flex items-center bg-indigo-100 text-indigo-800 text-sm font-medium px-3 py-1 rounded-full">
                {clientSearch}
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, client: "" });
                    setClientSearch(""); // Clear the search input when deselecting client
                  }}
                  className="ml-2 text-indigo-500 hover:text-indigo-700 focus:outline-none"
                >
                  &times;
                </button>
              </span>
            </div>
          )}
        </div>

        {/* Other Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="date"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Date
            </label>
            <input
              type="date"
              id="date"
              name="date"
              required
              value={formData.date}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label
              htmlFor="hour"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Heure
            </label>
            <input
              type="time"
              id="hour"
              name="hour"
              required
              value={formData.hour}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="recruteur"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Recruteur
            </label>
            <select
              id="recruteur"
              name="recruteur"
              value={formData.recruteur || ""}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md px-4 py-2 pt-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out"
            >
              <option value="" disabled>
                Choisir un recruteur
              </option>
              {recruiters.map((recr, index) => (
                <option key={index} value={recr._id}>
                  {recr.firstname} {recr.lastname}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="type"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Type d'entretien
            </label>
            <select
              id="type"
              name="type"
              required
              value={formData.type}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="présentiel">Présentiel</option>
              <option value="téléphonique">Téléphonique</option>
              <option value="visioconférence">Visioconférence</option>
            </select>
          </div>
        </div>

        <div>
          <LazyLoadingSelect
            qualifications={qualifications}
            handleChange={handleChange}
            formData={formData}
            setFormData={setFormData}
          />
        </div>

        <div>
          <label
            htmlFor="location"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Lieu
          </label>
          <input
            type="text"
            id="place"
            name="place"
            value={formData.place}
            onChange={handleChange}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label
            htmlFor="comment"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Notes complémentaires
          </label>
          <textarea
            id="comment"
            name="comment"
            rows={3}
            value={formData.comment}
            onChange={handleChange}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Other Form Fields (Recruteur, Poste, Notes, etc.) */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Confirmer
          </button>
        </div>
      </form>
    </Modal>
  );
};
